<template>
  <!--
    Pass gridOptions.context and remaining properties of gridOptions separately
    to avoid circular references between ag-Grid and Vue reactivity
    when the gridOptions.context references the parent Vue component,
    causing "Maximum call stack size exceeded" errors.
  -->
  <ag-grid-vue
    :gridOptions="gridOptionsExceptContext"
    :context="gridOptionsContext"
    :columnDefs="columnDefs"
    :selectionColumnDef="selectionColumnDef"
    :rowData="allColumnOptions"
    :suppressScrollOnNewData="true"
    :class="{ disabled }"
    @grid-ready="onGridReady"
    @row-data-updated="onRowDataUpdated"
    @selection-changed="onSelectionChanged"
  >
  </ag-grid-vue>
</template>
<script>
import AgGridVue from '@/components/grid/ag-grid-vue'
import _ from 'lodash'
import ResetToDefaultsHeaderButton from './ResetToDefaultsHeaderButton.vue'
import AgGridContextSplit from '@/mixins/AgGridContextSplit.js'

export default {
  components: {
    AgGridVue
  },
  mixins: [AgGridContextSplit],
  props: {
    allColumns: {
      type: Array,
      required: true
    },
    // The following props are defaulted to support Sync Fusion grid columns,
    // like how it's used by AbstractReport.
    // It can be overridden, such as for ag-grid.
    columnFieldKey: {
      type: String,
      default: 'field'
    },
    columnLabelKey: {
      type: String,
      default: 'headerText'
    },
    columnIsSelected: {
      type: Function,
      default: columnData => columnData.visible
    },
    extraColumnAttributes: {
      type: Array,
      default: () => ['width']
    },
    disabled: Boolean
  },
  emits: ['selectedColumnsUpdated', 'reset-to-defaults'],
  data () {
    return {
      gridOptions: {
        rowSelection: {
          mode: 'multiRow',
          enableClickSelection: false
        },
        rowDragManaged: true,
        suppressCellFocus: true,
        suppressRowHoverHighlight: true,
        suppressDragLeaveHidesColumns: true,
        // Source grid column visibility is managed by row selection on this column menu grid.
        // There is two-way binding between the allColumns prop and the selectedColumnsUpdated event.
        // Because row selection depends on the cell renderer, if a row isn't rendered, then even
        // though it may be visible in allColumns prop, it will end up getting deselected anyway
        // once the selectedColumnsUpdated event is emitted. To prevent this from happening, we
        // need to turn off virtualization. We do that by increasing rowBuffer to a higher value
        // then we ever expect to see.
        rowBuffer: 200,
        getRowId: data => {
          return data.data.field
        },
        defaultColDef: {
          suppressHeaderMenuButton: true
        }
      },
      gridApi: null,
      columnDefs: [
        {
          suppressMovable: true,
          rowDrag: true,
          width: 30
        },
        {
          suppressMovable: true,
          field: 'name',
          headerName: '',
          headerComponent: ResetToDefaultsHeaderButton,
          headerComponentParams: { parent: this },
          cellRenderer: params => params.value
        }
      ],
      selectionColumnDef: {
        width: 30
      },
      suppressSelectionChanged: false,
      lastEmittedSelectedColumns: null,
      selectionSyncTimeout: null
    }
  },
  computed: {
    allColumnOptions () {
      return this.allColumns.map(col => ({
        name: col[this.columnLabelKey],
        field: col[this.columnFieldKey],
        selected: this.columnIsSelected(col)
      }))
    },
    allColumnsByField () {
      return Object.fromEntries(this.allColumns.map(col => [col[this.columnFieldKey], {...col}]))
    }
  },
  beforeMount () {
    Object.assign(this.gridOptions, {
      context: {
        componentParent: this
      },
      onRowDragEnd: this.onRowDragEnd
    })
  },
  watch: {
    allColumns () {
      this.scheduleSelectionSyncFromProps()
    },
    allColumnOptions () {
      this.scheduleSelectionSyncFromProps()
    }
  },
  methods: {
    onGridReady (params) {
      this.gridApi = params.api
      this.scheduleSelectionSyncFromProps()
    },
    onRowDataUpdated () {
      this.scheduleSelectionSyncFromProps()
    },
    onSelectionChanged (event) {
      if (this.suppressSelectionChanged) return
      this.emitSelectedRows(event.api)
    },

    onRowDragEnd (event) {
      this.emitSelectedRows(event.api)
    },

    emitSelectedRows (gridApi) {
      const selectedColumns = _.sortBy(gridApi.getSelectedNodes(), node => node.rowIndex)
        .flatMap(node => {
          const field = node.data.field
          const col = this.allColumnsByField[field]
          if (!col) return [] // can happen on pivot table for group/pivot fields
          return [Object.assign(
            { [this.columnFieldKey]: field },
            Object.fromEntries(this.extraColumnAttributes.map(attr => [attr, col[attr]]))
          )]
        })

      // De-dupe event.
      if (_.isEqual(selectedColumns, this.lastEmittedSelectedColumns)) {
        return
      }

      this.lastEmittedSelectedColumns = selectedColumns
      this.$emit('selectedColumnsUpdated', selectedColumns)
    },

    getSelectedColumnsFromProps () {
      return this.allColumns.flatMap(col => {
        if (!this.columnIsSelected(col)) return []
        const field = col[this.columnFieldKey]
        return [Object.assign(
          { [this.columnFieldKey]: field },
          Object.fromEntries(this.extraColumnAttributes.map(attr => [attr, col[attr]]))
        )]
      })
    },

    syncSelectionFromProps () {
      if (!this.gridApi) return

      const selectedFields = new Set(this.getSelectedColumnsFromProps().map(col => col[this.columnFieldKey]))
      this.suppressSelectionChanged = true
      this.gridApi.forEachNode(node => {
        node.setSelected(selectedFields.has(node.data.field))
      })
      this.lastEmittedSelectedColumns = this.getSelectedColumnsFromProps()
      this.suppressSelectionChanged = false
    },

    scheduleSelectionSyncFromProps () {
      if (this.selectionSyncTimeout) {
        clearTimeout(this.selectionSyncTimeout)
      }

      this.selectionSyncTimeout = setTimeout(() => {
        this.gridApi?.redrawRows?.()
        this.syncSelectionFromProps()
        this.selectionSyncTimeout = null
      }, 100)
    },

    resetToDefaults () {
      this.$emit('reset-to-defaults')
    }
  },
  beforeUnmount () {
    if (this.selectionSyncTimeout) {
      clearTimeout(this.selectionSyncTimeout)
    }
  }
}
</script>
<style scoped>

.ag-grid :deep() {
  --background-color: white;

  width: 200px;
  height: 300px;

  .ag-header-container {
    background-color: var(--background-color);
  }

  .ag-body {
    background-color: var(--background-color);
  }

  .ag-selection-checkbox {
    margin-right: 0px;
  }

  .ag-header-cell {
    border-right: none;

    &::after {
      visibility: hidden;
    }
  }

  .ag-row {
    &.ag-row-even, &.ag-row-odd {
      background-color: var(--background-color);
    }
  }
}

.ag-grid.disabled :deep() {
  .ag-checkbox, .ag-drag-handle {
    pointer-events: none;
    opacity: .5;
  }
}

</style>
